import { useMemo } from 'react';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { axisTicks } from '../../domain/cars/accelerationTrace';
import { formatSpecNumber } from '../../domain/cars/carProperties';
import useWideStage from '../../application/motion/useWideStage';

/**
 * Both speed curves, drawn live, as the strip's own chart (SPEC-008 PR-8.9 –
 * PR-8.11).
 *
 * A strip says WHERE each car is. This says how fast it got there — each curve
 * rises out of the start line as its car pulls away, flattens where it runs out
 * of gears or grip, and arrives at the finish as a complete trace of the run the
 * reader just watched.
 *
 * EVERYTHING DRAWN HERE IS THE MODEL'S, which is why the verdict below still
 * prints what the model is anchored to. It was briefly the only acceleration
 * chart on the screen — PR-8.11 removed the static band of published road tests
 * on the argument that this one had taken over its vocabulary — and PR-8.14 put
 * that band back, because a screen that computes everything it draws needs one
 * plate on it that somebody measured. The two do not compete:
 * `CompareAccelerationChart.js` is on the page, drawing what two magazines timed
 * in 1991 against a time axis; this is on the stage, drawing what the physics
 * just did against a distance one.
 *
 * So it borrows the measured band's vocabulary rather than inventing one: the
 * same dotted grid, the same tick figures in the margin, the same 8-bit boxed
 * plot. A reader who has seen that chart — here or on a car's card — can read
 * this one without learning anything.
 *
 * BOTH CURVES ARE IN ONE BOX, overlaid, sharing one ceiling. Two plots stacked
 * are two pictures a reader compares by remembering the first while looking at
 * the second; one plot with two lines in it is a comparison they can simply see.
 *
 * THE AXIS IS DISTANCE, NOT TIME, and it earns that twice. Each curve is
 * revealed by the SAME fraction that positions its car, so the two tips are
 * always exactly where the two cars are; and the horizontal offset between those
 * tips IS the gap the strip reports in metres.
 *
 * The whole path is emitted once and REVEALED BY A CLIP, rather than rebuilt
 * from a growing slice of samples every frame. Rebuilding means a new `d` string
 * sixty times a second per lane; clipping means one transform the compositor
 * already knows how to do. A dash offset — the usual way to draw a line on —
 * advances along the PATH, which is longer where the curve is steep, so a tip
 * would run ahead of its car exactly where the car is accelerating hardest.
 */

/**
 * THE DRAWING BOX IS A PARAMETER, and the reason is the same one
 * `comparePlot.js` gives at more length: a viewBox scales with its box, so one
 * frame released from a 340px phone column onto the cinema stage's 1200px left
 * region magnifies 3.5x — 9px tick figures at 32px, 2px strokes at 7px, and a
 * plot that gets proportionally TALLER as it gets wider, on the one screen in
 * the product that must not scroll.
 *
 * So there are two, and `labelReach` travels WITH each of them. That number is a
 * hand-computed label width in viewBox units, and it is the thing in this file
 * that goes silently wrong in both directions when a frame moves: too small and
 * a marker's caption reaches out over the speed ticks, too large and every
 * caption in the first fifty metres is stacked on one x. Holding it on the frame
 * is what stops a second frame inheriting the first one's arithmetic.
 */
function raceFrame({ view, plot, labelReach }) {
    const width = view.width - plot.left - plot.right;
    const height = view.height - plot.top - plot.bottom;

    return {
        view, plot, width, height, labelReach,
        right: view.width - plot.right,
        floor: plot.top + height,
        viewBox: `0 0 ${view.width} ${view.height}`,
    };
}

/**
 * The phone's, unchanged to the unit.
 *
 * Shorter than the value chart's, and that is the whole point of this component
 * having its own: the strip and this have to fit on a phone together, and a
 * full-height plot underneath a full-height strip does not. `right` is wide
 * enough to seat the final distance tick, whose label is CENTRED on the axis's
 * end: at a hairline's inset the "400" a reader is measuring the whole chart
 * against is the one figure drawn half outside it.
 *
 * `labelReach` is 26 because the widest of these captions is "10.0 s spin", set
 * in a tabular face at the eight user units `raceChart.css` gives it — half of
 * that string is 26 units. A DOM measurement would be machinery for a string
 * this file can see.
 */
const NARROW_FRAME = raceFrame({
    view: { width: 320, height: 118 },
    plot: { left: 30, right: 16, top: 12, bottom: 18 },
    labelReach: 26,
});

/**
 * The cinema stage's: three and a half times the width for less than twice the
 * height, because a run down a strip is a landscape fact and the stage's own
 * budget is vertical.
 *
 * `labelReach` is the SAME 26, and that is a consequence rather than a copy: the
 * marker type is set in user units and this frame does not change it, so the
 * caption occupies the same units in a wider box. It is restated rather than
 * shared so that a frame which does change the type size has somewhere obvious
 * to say so.
 */
const WIDE_FRAME = raceFrame({
    view: { width: 720, height: 200 },
    plot: { left: 40, right: 22, top: 16, bottom: 26 },
    labelReach: 26,
});

/**
 * How far above the fastest car the speed axis runs.
 *
 * Scaled exactly to it, the winner's trap figure sits ON the frame and half of
 * it is drawn outside the plot. The distance axis needs no equivalent: the strip
 * ends at 400 metres and so does this, because they are the same 400 metres.
 */
const HEADROOM = 1.12;

/** Data space to viewBox space. One multiply per axis, and the y axis flips. */
function projector(frame, ceiling, distance) {
    const top = ceiling * HEADROOM;

    return {
        x: (metres) => frame.plot.left + (metres / distance) * frame.width,
        y: (kmh) => frame.floor - (kmh / top) * frame.height,
    };
}

/** A label's centre, held inside the plot rather than allowed into the margin. */
function clampLabel(frame, x) {
    return Math.min(
        Math.max(x, frame.plot.left + frame.labelReach),
        frame.right - frame.labelReach,
    );
}

function tracePath(lane, project) {
    if (!lane?.samples?.length) {
        return null;
    }

    return lane.samples
        .map((sample, index) => (
            `${index === 0 ? 'M' : 'L'}${project.x(sample.metres).toFixed(2)} ${project.y(sample.kmh).toFixed(2)}`
        ))
        .join(' ');
}

export default function RaceChart({
    ceiling,
    distance,
    lanes,
    markers,
    positions,
    seriesClasses,
}) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const frame = useWideStage() ? WIDE_FRAME : NARROW_FRAME;
    const project = useMemo(
        () => projector(frame, ceiling, distance),
        [ceiling, distance, frame],
    );
    const paths = useMemo(() => lanes.map((lane) => tracePath(lane, project)), [lanes, project]);

    if (!(ceiling > 0) || paths.every((path) => !path)) {
        return null;
    }

    const speed = (value) => formatSpecNumber(value, { languageCode });

    return (
        <figure className="race-chart">
            <p className="race-chart__axis-label" aria-hidden="true">{t('cars.accel.speedAxis')}</p>

            <svg
                className="race-chart__plot"
                viewBox={frame.viewBox}
                // Every figure this draws is on the lanes above it and in the
                // verdict below, and a pair of curves is not readable by ear at
                // any level of labelling.
                aria-hidden="true"
                focusable="false"
            >
                {/* The grid first, so every mark is drawn over it. */}
                <g className="race-chart__grid">
                    {axisTicks(ceiling).map((tick) => (
                        <line key={`speed-${tick}`} x1={frame.plot.left} x2={frame.right} y1={project.y(tick)} y2={project.y(tick)} />
                    ))}
                    {axisTicks(distance, { steps: [50, 100, 200], target: 4 }).map((tick) => (
                        <line key={`metres-${tick}`} x1={project.x(tick)} x2={project.x(tick)} y1={frame.plot.top} y2={frame.floor} />
                    ))}
                </g>

                {/* The two axes a reader measures against, solid where the grid
                    is dotted — the corner is the start line and has to look like
                    one. */}
                <path
                    className="race-chart__axis"
                    d={`M${frame.plot.left} ${frame.plot.top} L${frame.plot.left} ${frame.floor} L${frame.right} ${frame.floor}`}
                />

                <g className="race-chart__ticks">
                    {axisTicks(ceiling).map((tick) => (
                        <text key={`speed-label-${tick}`} x={frame.plot.left - 4} y={project.y(tick) + 3} textAnchor="end">
                            {speed(tick)}
                        </text>
                    ))}
                    {axisTicks(distance, { steps: [50, 100, 200], target: 4 }).map((tick) => (
                        <text key={`metres-label-${tick}`} x={project.x(tick)} y={frame.view.height - 6} textAnchor="middle">
                            {speed(tick)}
                        </text>
                    ))}
                </g>

                <defs>
                    {lanes.map((lane, index) => (
                        // One clip per lane, uniquely named, or the second curve
                        // reveals against the first one's rectangle.
                        <clipPath id={`race-trace-${index}-${lane.carId}`} key={lane.carId + String(index)}>
                            {/* Scaled from the origin, so the revealed width is
                                exactly the fraction of the strip that car has
                                covered — the same number that positions it. */}
                            <rect
                                width={frame.view.width}
                                height={frame.view.height}
                                transform={`scale(${Math.max(0, Math.min(1, positions[index]?.share ?? 0)).toFixed(4)} 1)`}
                            />
                        </clipPath>
                    ))}
                </defs>

                {paths.map((path, index) => (path ? (
                    <path
                        key={lanes[index].carId + String(index)}
                        className={`race-trace__line ${seriesClasses[index]}`}
                        d={path}
                        clipPath={`url(#race-trace-${index}-${lanes[index].carId})`}
                    />
                ) : null))}

                {/* Every moment the race has already been through, left where
                    it happened. The freeze explains one in words and then the
                    strip moves on and the words go; this is what is left of it,
                    so a reader arriving at the finish can still see where the
                    wheelspin was and where the lead changed. It is the job the
                    static band's measured points used to do on this screen, done
                    for the run the reader actually watched.

                    Labels alternate above and below by ORDER, not by lane: two
                    moments close together on the same curve is the crowding that
                    actually happens, and alternating by index answers it without
                    measuring anything. */}
                {(markers ?? []).map((marker, index) => (
                    <g
                        className={`race-chart__marker ${seriesClasses[marker.laneIndex]}`}
                        key={`${marker.key}-${marker.carId}-${index}`}
                    >
                        <circle cx={project.x(marker.metres)} cy={project.y(marker.kmh)} r={3} />
                        <text
                            // Held inside the plot. Most of these land in the
                            // first fifty metres — a launch and a wheelspin both
                            // happen there — and a centred label on a mark that
                            // close to the axis reaches out into the margin and
                            // sits on the speed ticks a reader is measuring
                            // against.
                            x={clampLabel(frame, project.x(marker.metres))}
                            y={project.y(marker.kmh) + (index % 2 === 0 ? -6 : 12)}
                            textAnchor="middle"
                        >
                            {t(`compare.moment.mark.${marker.key}`, marker.values)}
                        </text>
                    </g>
                ))}

                {/* The figure ON the leading edge of each curve — the same idiom
                    the car card's trace uses to label a measured point, doing
                    the same job here for the one figure that is moving. It rides
                    with the tip rather than sitting in a corner, because a number
                    beside the thing it describes needs no legend. */}
                {lanes.map((lane, index) => {
                    const position = positions[index];

                    if (!position || position.share <= 0) {
                        return null;
                    }

                    // The first lane's figure sits ABOVE its mark and the
                    // second's BELOW it, always. Two cars matched closely enough
                    // to be worth comparing are two cars whose tips spend the
                    // race within a few units of each other, so a shared side
                    // means two numbers on top of each other exactly when the
                    // race is closest — and a crowding rule that measured them
                    // would be arithmetic guarding a case a constant already
                    // answers.
                    const above = index === 0;

                    return (
                        <g className={`race-chart__figure ${seriesClasses[index]}`} key={`tip-${lane.carId}-${index}`}>
                            <circle cx={project.x(position.metres)} cy={project.y(position.kmh)} r={2.5} />
                            <text
                                x={project.x(position.metres) - 5}
                                y={project.y(position.kmh) + (above ? -5 : 10)}
                                textAnchor="end"
                            >
                                {speed(position.kmh)}
                            </text>
                        </g>
                    );
                })}
            </svg>

            <p className="race-chart__axis-label race-chart__axis-label--end" aria-hidden="true">
                {t('compare.race.chartAxis')}
            </p>
        </figure>
    );
}
